import { useAuthentication, useConfirmForgotPassword, useForgotPassword } from '@psyomics/ui-common';
import { useTranslation } from 'react-i18next';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';

import { BackLink } from '../../../molecules/back-link/back-link';
import { ConfirmForgotPasswordForm } from '../confirm-forgot-password-form/confirm-forgot-password-form';

const createResendHandler =
  ({
    forgotPassword,
    email,
    t,
  }: {
    forgotPassword: ReturnType<typeof useForgotPassword>;
    email: string;
    t: (key: string) => string;
  }) =>
  async () => {
    try {
      await forgotPassword({ username: email });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error('resend code failed');
      }

      throw new Error(t('common.errors.networkError'));
    }
  };

const ConfirmForgotPassword = () => {
  const { t } = useTranslation();
  const { apiUrl } = useAuthentication();
  const navigate = useNavigate();
  const username = useLocation().state?.username;
  const forgotPassword = useForgotPassword(apiUrl);
  const confirmForgotPassword = useConfirmForgotPassword(apiUrl);

  if (!username) return <Navigate replace to="/forgot-password" />;

  const handleResendCode = createResendHandler({ forgotPassword, email: username, t });

  const handleSubmit = async ({
    confirmationCode,
    password,
    confirmPassword,
  }: {
    confirmationCode: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (password !== confirmPassword) {
      throw new Error(t('auth.common.errors.passwordsDoNotMatch'));
    }

    try {
      await confirmForgotPassword({ username, confirmationCode, password });
      navigate('/forgot-password/success');
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(t('common.errors.networkError'));
    }
  };

  return (
    <div className="form-container">
      <BackLink text={t('common.buttons.goBack')} />
      <ConfirmForgotPasswordForm email={username} onResendCode={handleResendCode} onSubmit={handleSubmit} />
    </div>
  );
};

export { ConfirmForgotPassword };
